// ============================================================
// SecaoComparativo.js — Relatório comparativo entre meses (Fase 5)
//
// Compara os ÚLTIMOS 3 MESES (o atual + os 2 anteriores) em 3 visões:
//   1. Frequência média do núcleo inteiro, mês a mês.
//   2. Frequência média por modalidade, mês a mês.
//   3. Evolução de UM atleta específico, mês a mês.
//
// Busca os attendance_logs do núcleo UMA VEZ (sem filtro de data — mesmo
// padrão já usado em SecaoRelatorio.js) e fatia em 3 meses no navegador,
// rodando calcularFrequencia() uma vez por mês. Nenhuma lógica de negócio
// nova — tudo vem de calculo.js, a fonte única de sempre.
// ============================================================

import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { carregarDispensasDoPeriodo, carregarNomesAtuais, carregarPresencasDoPeriodo } from "../firebase";
import { intervaloDosMeses } from "../leituras";
import { calcularFrequencia, normalizarModalidade, mediaPctTotal } from "../calculo";
import { MESES, CINZA, CINZA_ESC, TEXTO, AZUL, argbParaCss } from "../tema";
import { Card, Btn, Sel, StatusBox, Vazio } from "../componentes";
import { gerarPDFComparativo } from "../pdfRelatorio";
import { useNomeNucleo } from "../NucleosContexto";
import useIsMobile from "../useIsMobile";
import SemNucleo from "../componentes/SemNucleo";

// Paleta pras linhas do gráfico "por modalidade" (uma cor por modalidade).
const CORES_LINHA = ["#DC2626", "#2563EB", "#16A34A", "#D97706", "#7C3AED", "#DB2777", "#0891B2", "#64748B"];

export default function SecaoComparativo({ userNucleo, isAdmin, modalidadeFixa = null }) {
  const isMobile = useIsMobile();
  // Quando modalidadeFixa vem preenchida (uso pelo técnico), o comparativo
  // mostra SÓ os atletas e a linha da modalidade dele — e o seletor de
  // núcleo some (ele é fixo no núcleo dele). Sem a prop, comporta-se
  // exatamente como antes (coordenador/admin veem tudo).
  const modalidadeFixaNorm = modalidadeFixa ? normalizarModalidade(modalidadeFixa) : null;
  // Núcleo vem do seletor global do cabeçalho (userNucleo). Não há mais
  // estado/seletor próprio aqui — assim o Comparativo sempre acompanha o
  // núcleo escolhido lá em cima (antes ficava travado no núcleo inicial).
  const nucleo = userNucleo || "";
  const [carregando, setCarregando] = useState(false);
  const [status, setStatus] = useState(null);
  const [dados, setDados] = useState(null); // { meses, porNucleo, modalidades, porModalidade, porAtleta, nomesAtletas }
  const [atletaEscolhido, setAtletaEscolhido] = useState("");
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const nomeNucleo = useNomeNucleo();

  // Ao trocar o núcleo no seletor global, limpa os resultados na tela — assim
  // não ficam dados do núcleo anterior visíveis. O usuário clica em "Comparar"
  // de novo e os dados vêm do núcleo agora selecionado.
  useEffect(() => {
    setDados(null);
    setAtletaEscolhido("");
    setStatus(null);
  }, [userNucleo]);

  // Intervalo selecionável (padrão: últimos 3 meses). Limite de 12 meses
  // por vez — mais que isso pesa a tela sem ganhar leitura.
  const hoje = new Date();
  const doisAtras = new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1);
  const [mesInicio, setMesInicio] = useState(String(doisAtras.getMonth()));
  const [anoInicio, setAnoInicio] = useState(String(doisAtras.getFullYear()));
  const [mesFim, setMesFim] = useState(String(hoje.getMonth()));
  const [anoFim, setAnoFim] = useState(String(hoje.getFullYear()));
  const anosDisponiveis = [hoje.getFullYear() - 2, hoje.getFullYear() - 1, hoje.getFullYear()].map(String);

  // Monta a lista de meses do intervalo escolhido (inclusive nas pontas).
  // Devolve null se o intervalo for inválido (fim antes do início) ou
  // maior que 12 meses.
  function mesesDoIntervalo() {
    const ini = new Date(parseInt(anoInicio), parseInt(mesInicio), 1);
    const fim = new Date(parseInt(anoFim), parseInt(mesFim), 1);
    if (fim < ini) return null;
    const lista = [];
    const d = new Date(ini);
    while (d <= fim && lista.length <= 12) {
      lista.push({ mes: d.getMonth(), ano: d.getFullYear(), label: `${MESES[d.getMonth()].slice(0, 3)}/${d.getFullYear()}` });
      d.setMonth(d.getMonth() + 1);
    }
    if (lista.length > 12) return null;
    return lista;
  }



  // Os dois gráficos num arquivo só, no padrão dos outros relatórios.
  // A evolução por atleta não entra: é leitura individual, e um PDF com
  // um gráfico por atleta viraria um documento que ninguém lê inteiro.
  async function baixarPDF() {
    if (!dados) return;
    setGerandoPDF(true);
    setStatus({ tipo: "info", msg: "Gerando o comparativo em PDF..." });
    try {
      const nomeArquivo = await gerarPDFComparativo({
        porNucleo: dados.porNucleo,
        modalidades: dados.modalidades,
        porModalidade: dados.porModalidade,
        nucleo,
        nucleoNome: nomeNucleo(nucleo),
        periodo: dados.meses.length
          ? `${dados.meses[0]} a ${dados.meses[dados.meses.length - 1]}`
          : "",
      });
      setStatus({ tipo: "ok", msg: `✅ Arquivo "${nomeArquivo}" gerado e baixado!` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao gerar o PDF: " + (e.message || "tente novamente.") });
    } finally {
      setGerandoPDF(false);
    }
  }

  async function buscarComparativo() {
    const intervalo = mesesDoIntervalo();
    if (!intervalo) {
      setStatus({ tipo: "erro", msg: "Intervalo inválido: o mês final deve ser igual ou depois do inicial, e no máximo 12 meses por vez." });
      return;
    }
    setCarregando(true);
    setStatus({ tipo: "info", msg: `Buscando dados de ${intervalo[0].label} a ${intervalo[intervalo.length - 1].label}...` });
    try {
      const tresMeses = intervalo;

      // O comparativo olha até 12 meses, e pede exatamente esses meses:
      // antes trazia o histórico inteiro, que no ano que vem será o dobro.
      const todosRegistros = await carregarPresencasDoPeriodo(
        nucleo, intervaloDosMeses(tresMeses).de, intervaloDosMeses(tresMeses).ate,
      );
      const todasDispensas = await carregarDispensasDoPeriodo(
        nucleo, intervaloDosMeses(tresMeses).de, intervaloDosMeses(tresMeses).ate,
      );
      const nomesAtuais = await carregarNomesAtuais(nucleo);

      const porMes = tresMeses.map(({ mes, ano, label }) => {
        const registrosDoMes = todosRegistros.filter(r => {
          // Mesma normalização das outras telas e do calculo.js.
          const dk = r.dateKey || (r.data ? r.data.split("/").reverse().join("-") : "");
          const [y, m] = (dk || "").split("-");
          return parseInt(y) === ano && parseInt(m) === mes + 1;
        });
        const dispensasDoMes = todasDispensas.filter(d => {
          const [y, m] = d.data.split("-");
          return parseInt(y) === ano && parseInt(m) === mes + 1;
        });
        const calc = calcularFrequencia({ registros: registrosDoMes, dispensas: dispensasDoMes, mes, ano, nomesAtuais });
        return { label, calc };
      });

      // Filtra pela modalidade do técnico quando modalidadeFixa está ativa;
      // sem ela, devolve todos (comportamento do coordenador/admin intacto).
      const ehDaModalidade = (membros) => modalidadeFixaNorm
        ? membros.filter(m => normalizarModalidade(m.modalidade) === modalidadeFixaNorm)
        : membros;

      // 1) Núcleo geral: média de % total entre os atletas, mês a mês.
      const porNucleo = porMes.map(({ label, calc }) => {
        const atletas = ehDaModalidade(calc.membros).filter(m => m.grupo === "ATLETA");
        const media = mediaPctTotal(atletas) ?? 0;
        return { mes: label, media: Math.round(media * 100) };
      });

      // 2) Por modalidade: uma linha por modalidade encontrada em qualquer um dos 3 meses.
      const modalidades = Array.from(new Set(
        porMes.flatMap(({ calc }) => ehDaModalidade(calc.membros).filter(m => m.grupo === "ATLETA").map(m => m.modalidade))
      )).sort();
      const porModalidade = porMes.map(({ label, calc }) => {
        const linha = { mes: label };
        modalidades.forEach(mod => {
          const atletasMod = calc.membros.filter(m => m.grupo === "ATLETA" && m.modalidade === mod);
          const mMod = mediaPctTotal(atletasMod);
          linha[mod] = mMod === null ? null : Math.round(mMod * 100);
        });
        return linha;
      });

      // 3) Por atleta: nome -> [{ mes, pct, classif }] pra evolução individual.
      const nomesAtletas = Array.from(new Set(
        porMes.flatMap(({ calc }) => ehDaModalidade(calc.membros).filter(m => m.grupo === "ATLETA").map(m => m.nome))
      )).sort();
      const porAtleta = {};
      nomesAtletas.forEach(nome => {
        porAtleta[nome] = porMes.map(({ label, calc }) => {
          const m = calc.membros.find(x => x.nome === nome && x.grupo === "ATLETA");
          return { mes: label, pct: m ? Math.round(m.pctTotal * 100) : null, classif: m ? m.classif : null };
        });
      });

      setDados({ meses: tresMeses.map(m => m.label), porNucleo, modalidades, porModalidade, porAtleta, nomesAtletas });
      setAtletaEscolhido(nomesAtletas[0] || "");
      setStatus({ tipo: "ok", msg: `Comparativo de ${tresMeses[0].label} a ${tresMeses[tresMeses.length - 1].label} pronto.` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao buscar comparativo: " + (e.message || "") });
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }

  // Sem núcleo escolhido, a tela diz isso em vez de carregar o de
  // outra pessoa. Depois dos hooks, de propósito: sair antes deles
  // quebraria a ordem que o React exige.
  if (!nucleo) return <SemNucleo oQueFaria="comparar os meses deste núcleo" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Configurar */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
          Comparativo entre meses{modalidadeFixaNorm ? ` · ${modalidadeFixaNorm}` : ""}
        </div>
        <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 16 }}>
          Escolha o intervalo (até 12 meses): núcleo geral, por modalidade e por atleta.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
          <Sel label="De (mês)" value={mesInicio} onChange={setMesInicio}
            options={MESES.map((m, i) => ({ value: String(i), label: m }))} />
          <Sel label="De (ano)" value={anoInicio} onChange={setAnoInicio} options={anosDisponiveis} />
          <Sel label="Até (mês)" value={mesFim} onChange={setMesFim}
            options={MESES.map((m, i) => ({ value: String(i), label: m }))} />
          <Sel label="Até (ano)" value={anoFim} onChange={setAnoFim} options={anosDisponiveis} />
        </div>
        <Btn onClick={buscarComparativo} disabled={carregando} style={{ fontSize: 15, padding: "12px 24px", width: "100%" }}>
          {carregando ? "Buscando..." : "📈 Comparar período"}
        </Btn>
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {dados && (
        <>
          {/* O documento sai com os dois gráficos e a tabela dos valores,
              no mesmo padrão do relatório geral e do por modalidade. */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Comparativo em PDF</div>
                <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 3, lineHeight: 1.55 }}>
                  A média do núcleo e o comparativo das modalidades no mesmo arquivo, com
                  cabeçalho, rodapé e os valores em tabela.
                </div>
              </div>
              <Btn onClick={baixarPDF} disabled={gerandoPDF} style={{ background: "#7C3AED" }}>
                {gerandoPDF ? "Gerando..." : "📄 Baixar PDF"}
              </Btn>
            </div>
          </Card>

          {/* 1) Núcleo geral — oculto para o técnico (ele vê só a
              modalidade dele, então este bloco seria redundante com o de
              baixo). Coordenador/admin continuam vendo normalmente. */}
          {!modalidadeFixaNorm && (
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Frequência média do núcleo</div>
            <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12 }}>Média do % total entre todos os atletas, mês a mês</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dados.porNucleo} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 12, fill: TEXTO }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: CINZA_ESC }} unit="%" />
                <Tooltip formatter={(v) => [`${v}%`, "Média"]} />
                <Line type="monotone" dataKey="media" stroke={AZUL} strokeWidth={3} dot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          )}

          {/* 2) Por modalidade */}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
              {modalidadeFixaNorm ? `Frequência da modalidade · ${modalidadeFixaNorm}` : "Frequência por modalidade"}
            </div>
            <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12 }}>Média do % total{modalidadeFixaNorm ? "" : " por modalidade"}, mês a mês</div>
            {dados.modalidades.length === 0 ? (
              <Vazio icone="📈" titulo="Nenhum atleta com registros no período">
                Escolha um intervalo que inclua meses com treino lançado.
              </Vazio>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={dados.porModalidade} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: TEXTO }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: CINZA_ESC }} unit="%" />
                  <Tooltip formatter={(v) => (v === null ? ["—", ""] : [`${v}%`, ""])} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {dados.modalidades.map((mod, i) => (
                    <Line key={mod} type="monotone" dataKey={mod} name={mod} stroke={CORES_LINHA[i % CORES_LINHA.length]} strokeWidth={3} dot={{ r: 5 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* 3) Por atleta */}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Evolução por atleta</div>
            {dados.nomesAtletas.length === 0 ? (
              <Vazio icone="📈" titulo="Nenhum atleta com registros no período">
                Escolha um intervalo que inclua meses com treino lançado.
              </Vazio>
            ) : modalidadeFixaNorm ? (
              // Técnico: todos os atletas da modalidade, em sequência — é só
              // rolar a tela, sem precisar selecionar um por um.
              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                {dados.nomesAtletas.map(nome => (
                  <div key={nome}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: TEXTO, marginBottom: 8 }}>{nome}</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={dados.porAtleta[nome] || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fontSize: 12, fill: TEXTO }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: CINZA_ESC }} unit="%" />
                        <Tooltip formatter={(v) => (v === null ? ["—", ""] : [`${v}%`, "% Total"])} />
                        <Line type="monotone" dataKey="pct" stroke="#7C3AED" strokeWidth={3} dot={{ r: 5 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 12 }}>
                      {(dados.porAtleta[nome] || []).map((p, i) => (
                        <div key={i} style={{ background: CINZA, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: CINZA_ESC, marginBottom: 2 }}>{p.mes}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: p.classif ? argbParaCss(p.classif.cor) : CINZA_ESC }}>
                            {p.pct === null ? "—" : `${p.pct}%`}
                          </div>
                          <div style={{ fontSize: 10.5, color: CINZA_ESC, marginTop: 2 }}>{p.classif ? p.classif.label : "sem registro"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div style={{ maxWidth: 300, marginBottom: 16 }}>
                  <Sel label="Atleta" value={atletaEscolhido} onChange={setAtletaEscolhido}
                    options={dados.nomesAtletas} />
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dados.porAtleta[atletaEscolhido] || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="mes" tick={{ fontSize: 12, fill: TEXTO }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: CINZA_ESC }} unit="%" />
                    <Tooltip formatter={(v) => (v === null ? ["—", ""] : [`${v}%`, "% Total"])} />
                    <Line type="monotone" dataKey="pct" stroke="#7C3AED" strokeWidth={3} dot={{ r: 5 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 14 }}>
                  {(dados.porAtleta[atletaEscolhido] || []).map((p, i) => (
                    <div key={i} style={{ background: CINZA, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: CINZA_ESC, marginBottom: 2 }}>{p.mes}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: p.classif ? argbParaCss(p.classif.cor) : CINZA_ESC }}>
                        {p.pct === null ? "—" : `${p.pct}%`}
                      </div>
                      <div style={{ fontSize: 10.5, color: CINZA_ESC, marginTop: 2 }}>{p.classif ? p.classif.label : "sem registro"}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </div>
  );
}